import { createSelector } from 'reselect';
import { getChannelForWidget } from '@wix/wix-vod-shared/dist/src/common/selectors/channels';
import * as MOBILE_VIDEOS_PER_PAGE_COUNT from '@wix/wix-vod-constants/dist/mobile/videos-per-page-count';
import {
  getAllSettings,
  getNumberOfRows,
  isStripLayout,
  isClassicLayout,
  getVideosInRowCount,
  isCompactLayout,
  isGridLayout,
  isGridLayoutMobile,
} from 'shared/selectors/app-settings';
import { isMobile } from 'shared/utils/device/screen';
import { isNumberOfRowsEnabled } from 'shared/selectors/experiments';
import { videosPerPage } from 'widget/utils/videos-sizes/videos-sizes';
import {
  COMPACT_NUMBER_OF_ROWS,
  COMPACT_VIDEOS_IN_ROW,
} from '@wix/wix-vod-constants/dist/app-settings/compact-video-counts';
import { VIDEO_LIST_ITEM_WIDTH } from 'shared/constants/lightbox';
import { getMaxVideosCountInRow as getGridVideosPerRow } from 'widget/layouts/grid/selectors';
import { getVideosGroupedByIds } from 'shared/selectors/videos';

export const getVideosPerPageCount = createSelector(
  getAllSettings,
  getNumberOfRows,
  getVideosInRowCount,
  getGridVideosPerRow,
  getChannelForWidget,
  isNumberOfRowsEnabled,
  isStripLayout,
  isClassicLayout,
  isCompactLayout,
  isGridLayout,
  isGridLayoutMobile,
  state => state.windowSize,
  state => state.isLightbox,
  (
    appSettings,
    numberOfRows,
    videosInRow,
    gridVideosInRow,
    channelData,
    isNumberOfRowsEnabled,
    isStrip,
    isClassic,
    isCompact,
    isGrid,
    isGridMobile,
    windowSize,
    isLightbox,
  ) => {
    if (!appSettings) {
      return 0;
    }

    if (isMobile()) {
      return isGridMobile
        ? MOBILE_VIDEOS_PER_PAGE_COUNT.GRID_LAYOUT
        : MOBILE_VIDEOS_PER_PAGE_COUNT.BASIC_LAYOUT;
    }

    if (isLightbox) {
      return Math.ceil(window.innerWidth / VIDEO_LIST_ITEM_WIDTH);
    }

    if (isGrid) {
      return gridVideosInRow * numberOfRows;
    }

    if (isCompact) {
      return COMPACT_NUMBER_OF_ROWS * COMPACT_VIDEOS_IN_ROW;
    }

    if (isStrip) {
      return videosInRow * numberOfRows;
    }

    if (isClassic && isNumberOfRowsEnabled) {
      return videosInRow * numberOfRows;
    }

    return videosPerPage(
      windowSize,
      videosInRow,
      _.get(channelData, 'videosCount', 0),
      appSettings,
      channelData || {},
    );
  },
);

export const getListVideoIds = state => state.lazyChannelVideos.ids;
export const getPrependedVideoIds = state =>
  state.lazyChannelVideos.prependedIds;

export const getVideoIds = createSelector(
  getPrependedVideoIds,
  getListVideoIds,
  (prependedIds, ids) => [...prependedIds, ..._.without(ids, ...prependedIds)],
);

export const getChannelVideos = createSelector(
  getVideoIds,
  getVideosGroupedByIds,
  (channelVideoIds, allVideos) => _.map(channelVideoIds, id => allVideos[id]),
);

export const getCursor = state => state.lazyChannelVideos.cursor;
export const getCurrentPageIndex = state =>
  state.lazyChannelVideos.currentPageIndex;
export const getCurrentVideoIndex = state =>
  state.lazyChannelVideos.currentVideoIndex;
export const getIsFetching = state => state.lazyChannelVideos.isFetching;

export const getVideoIdsByPageNumber = createSelector(
  getVideoIds,
  getVideosPerPageCount,
  (ids, videosPerPage) => _.chunk(ids, videosPerPage),
);

export const hasPrevVideo = createSelector(
  getVideoIds,
  getCurrentVideoIndex,
  (ids, index) => Boolean(ids[index - 1]) && index !== -1,
);

export const hasNextVideo = createSelector(
  getVideoIds,
  getCurrentVideoIndex,
  (ids, index) => Boolean(ids[index + 1]) && index !== -1,
);

export const getNextVideoId = createSelector(
  getVideoIds,
  getCurrentVideoIndex,
  (ids, index) => ids[index + 1] || null,
);
